<template>
    <v-container fluid class="mb-12 mb-lg-0">
        <v-row justify="center">
            <v-col cols="12">
                <v-card width="100%">
                    <v-card-title class="d-block py-2 text-center font-weight-bold primary secondary--text" style="word-break: normal">
                        RETIROS WALLET
                    </v-card-title>
                    <v-btn bottom color="secondary primary--text" depressed fab absolute right small @click="getWithdrawals(true)">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="align-center flex-wrap">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            label="Buscar"
                            single-line
                            hide-details
                            class="mr-md-4"/>
                        <v-spacer/>
                        <v-select
                            v-model="status"
                            :items="statuses"
                            item-text="text"
                            item-value="value"
                            label="Estado"
                            dense
                            outlined
                            hide-details
                            style="max-width: 220px"
                            @change="getWithdrawals(true)"/>
                    </v-card-title>
                    <v-data-table
                        :headers="headers"
                        :items="withdrawals"
                        :search="search"
                        :loading="loading"
                        :no-data-text="noDataText"
                        :footer-props="footerProps">
                        <template v-slot:item.id="{ item }">
                            <strong>{{ item.id }}</strong>
                        </template>
                        <template v-slot:item.customer="{ item }">
                            <div class="font-weight-medium" style="text-transform: capitalize">
                                {{ item.customer ? item.customer.toLowerCase() : '' }}
                            </div>
                            <div class="caption primary--text font-weight-bold">{{ item.nickname }}</div>
                        </template>
                        <template v-slot:item.amount="{ item }">
                            <strong>{{ item.amount }}</strong>
                        </template>
                        <template v-slot:item.payout_amount="{ item }">
                            <strong v-if="item.payout_amount">{{ item.payout_amount }}</strong>
                            <span v-else>--</span>
                        </template>
                        <template v-slot:item.payout_method_label="{ item }">
                            <v-chip small outlined color="primary">
                                {{ item.payout_method_label }}
                            </v-chip>
                        </template>
                        <template v-slot:item.payout_info="{ item }">
                            <div v-if="item.payout_account || item.payout_reference" class="caption">
                                <div v-if="item.payout_account" class="font-weight-medium">
                                    {{ item.payout_account.label }}
                                </div>
                                <div v-if="item.payout_reference">Ref: {{ item.payout_reference }}</div>
                                <v-btn
                                    v-if="item.payout_voucher_url"
                                    x-small
                                    text
                                    color="primary"
                                    :href="item.payout_voucher_url"
                                    target="_blank">
                                    <v-icon x-small left>mdi-paperclip</v-icon>Comprobante
                                </v-btn>
                            </div>
                            <span v-else>--</span>
                        </template>
                        <template v-slot:item.status_label="{ item }">
                            <v-chip small dark :color="statusColor(item.status)">
                                {{ item.status_label }}
                            </v-chip>
                        </template>
                        <template v-slot:item.note="{ item }">
                            <span class="text-truncate d-inline-block" style="max-width: 260px">{{ item.note }}</span>
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-btn
                                color="success"
                                small
                                depressed
                                dark
                                :disabled="!canReview(item) || loading"
                                @click="openApprove(item)"
                                style="margin: 1px">
                                <v-icon>mdi-check-circle</v-icon>
                            </v-btn>
                            <v-btn
                                color="error"
                                small
                                depressed
                                dark
                                :disabled="!canReview(item) || loading"
                                @click="openReject(item)"
                                style="margin: 1px">
                                <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="approveDialog" persistent scrollable max-width="720px">
            <v-card class="withdrawal-fixed-dialog">
                <v-card-title class="primary secondary--text withdrawal-fixed-header d-block text-center" style="word-break: normal">
                    Aprobar retiro wallet
                </v-card-title>
                <v-card-text class="withdrawal-fixed-body pt-4" v-if="selected">
                    <v-alert dense text type="warning">
                        Confirma esta accion solo cuando el dinero ya fue entregado o enviado al cliente.
                    </v-alert>
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Cliente</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.customer }} - {{ selected.nickname }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Debito wallet</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.amount }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="selected.payout_amount">
                            <v-list-item-content>
                                <v-list-item-title>Entrega al cliente</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.payout_amount }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Forma de retiro</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.payout_method_label }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="selected.agent">
                            <v-list-item-content>
                                <v-list-item-title>Corresponsal/Agente</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ selected.agent.name }} - {{ selected.agent.address }}, {{ selected.agent.city }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle v-if="selected.agent.opening_hours">
                                    {{ selected.agent.opening_hours }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="selected.bank_account">
                            <v-list-item-content>
                                <v-list-item-title>Cuenta bancaria</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ selected.bank_account.bank_name }} - {{ selected.bank_account.account_type }} {{ selected.bank_account.account_mask }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle>
                                    {{ selected.bank_account.holder_name }} - {{ selected.bank_account.holder_document }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="selected.rate_label">
                            <v-list-item-content>
                                <v-list-item-title>Tasa</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.rate_label }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="selected.quote">
                            <v-list-item-content>
                                <v-list-item-title>Comisiones</v-list-item-title>
                                <v-list-item-subtitle>
                                    Wallet: {{ feeText(selected.quote, 'source') }} - Entrega: {{ feeText(selected.quote, 'target') }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="selected.note">
                            <v-list-item-content>
                                <v-list-item-title>Nota</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.note }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <v-divider class="my-4"/>
                    <v-alert dense text type="info" v-if="!availablePayoutAccounts.length">
                        No hay cuentas operativas activas disponibles para pagar este retiro.
                    </v-alert>
                    <v-form ref="approveFormRef" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="approveForm.payout_account_id"
                                    :items="availablePayoutAccounts"
                                    item-text="label"
                                    item-value="id"
                                    label="Cuenta que entrega el dinero"
                                    outlined
                                    dense
                                    :disabled="loading || availablePayoutAccounts.length <= 1"
                                    :rules="[v => !!v || 'Seleccione la cuenta']"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model.trim="approveForm.payout_reference"
                                    label="Referencia del pago"
                                    outlined
                                    dense
                                    maxlength="80"
                                    :disabled="loading"
                                    :rules="[v => !!(v || '').trim() || 'Ingrese la referencia']"/>
                            </v-col>
                            <v-col cols="12">
                                <v-file-input
                                    v-model="approveForm.payout_voucher"
                                    label="Comprobante"
                                    accept="image/jpeg,image/png,application/pdf"
                                    prepend-icon="mdi-paperclip"
                                    outlined
                                    dense
                                    show-size
                                    :disabled="loading"
                                    :rules="[v => !!v || 'Adjunte el comprobante']"/>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="withdrawal-fixed-footer">
                    <v-btn color="error" text @click="closeDialogs" :disabled="loading">
                        <v-icon>mdi-close-circle</v-icon>&nbsp;Cerrar
                    </v-btn>
                    <v-spacer/>
                    <v-btn color="success" :loading="loading" :disabled="loading || !approveReady" @click="submitApprove">
                        <v-icon>mdi-check-circle</v-icon>&nbsp;Aprobar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rejectDialog" persistent scrollable max-width="560px">
            <v-card class="withdrawal-fixed-dialog">
                <v-card-title class="primary secondary--text withdrawal-fixed-header d-block text-center" style="word-break: normal">
                    Rechazar retiro wallet
                </v-card-title>
                <v-card-text class="withdrawal-fixed-body pt-4" v-if="selected">
                    <v-alert dense text type="info">
                        Al rechazar se devuelve el monto al saldo disponible del cliente.
                    </v-alert>
                    <p class="mb-1"><strong>{{ selected.customer }} - {{ selected.nickname }}</strong></p>
                    <p class="mb-1">{{ selected.amount }}</p>
                    <p class="mb-3" v-if="selected.payout_amount">Entrega: {{ selected.payout_amount }}</p>
                    <v-textarea
                        v-model="reason"
                        label="Motivo"
                        rows="3"
                        :disabled="loading"/>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="withdrawal-fixed-footer">
                    <v-btn color="error" text @click="closeDialogs" :disabled="loading">
                        <v-icon>mdi-close-circle</v-icon>&nbsp;Cerrar
                    </v-btn>
                    <v-spacer/>
                    <v-btn color="error" :loading="loading" :disabled="loading" @click="submitReject">
                        <v-icon>mdi-close-circle</v-icon>&nbsp;Rechazar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: "WalletWithdrawals",
    props: ['user'],
    data: () => ({
        search: '',
        status: 'pending',
        statuses: [
            { text: 'Pendientes', value: 'pending' },
            { text: 'Aprobados', value: 'approved' },
            { text: 'Rechazados', value: 'rejected' },
            { text: 'Todos', value: 'all' }
        ],
        headers: [
            { text: 'ID', value: 'id' },
            { text: 'Cliente', value: 'customer' },
            { text: 'Correo', value: 'email' },
            { text: 'Wallet', value: 'amount', align: 'end' },
            { text: 'Entrega', value: 'payout_amount', align: 'end' },
            { text: 'Metodo', value: 'payout_method_label', align: 'center' },
            { text: 'Pago', value: 'payout_info' },
            { text: 'Estado', value: 'status_label', align: 'center' },
            { text: 'Nota', value: 'note' },
            { text: 'Creado', value: 'created_at' },
            { text: 'Revisado Por', value: 'reviewed_by' },
            { text: 'Accion', value: 'action', align: 'center', filterable: false, sortable: false }
        ],
        withdrawals: [],
        payoutAccounts: [],
        loading: true,
        approveDialog: false,
        rejectDialog: false,
        selected: null,
        approveForm: {
            payout_account_id: null,
            payout_reference: '',
            payout_voucher: null
        },
        reason: '',
        noDataText: 'No hay retiros wallet para mostrar',
        footerProps: {
            itemsPerPageOptions: [25, 50, 100, 250, -1]
        }
    }),
    computed: {
        availablePayoutAccounts() {
            if (!this.selected || !this.selected.payout_currency) {
                return this.payoutAccounts;
            }

            return this.payoutAccounts.filter(account => !account.currency || account.currency === this.selected.payout_currency);
        },
        approveReady() {
            return !!this.selected
                && !!this.approveForm.payout_account_id
                && !!(this.approveForm.payout_reference || '').trim()
                && !!this.approveForm.payout_voucher;
        }
    },
    created() {
        this.getWithdrawals();
    },
    methods: {
        getWithdrawals(reset) {
            this.loading = true;
            if (reset === true) {
                this.withdrawals = [];
            }

            axios.get('/api/wallet/withdrawals', { params: { status: this.status } }).then(res => {
                this.withdrawals = res.data.withdrawals || [];
                this.payoutAccounts = res.data.accounts || [];
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar los retiros wallet.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        openApprove(item) {
            this.selected = item;
            const accounts = this.accountsForWithdrawal(item);

            this.approveForm = {
                payout_account_id: item.payout_account ? item.payout_account.id : (accounts[0] ? accounts[0].id : null),
                payout_reference: item.payout_reference || '',
                payout_voucher: null
            };
            this.approveDialog = true;
        },
        openReject(item) {
            this.selected = item;
            this.reason = '';
            this.rejectDialog = true;
        },
        closeDialogs() {
            this.approveDialog = false;
            this.rejectDialog = false;
            this.selected = null;
            this.approveForm = {
                payout_account_id: null,
                payout_reference: '',
                payout_voucher: null
            };
            this.reason = '';
        },
        submitApprove() {
            if (!this.selected) return;
            if (this.$refs.approveFormRef && !this.$refs.approveFormRef.validate()) return;
            if (!this.approveReady) {
                this.$toast.warning('Complete la cuenta, referencia y comprobante del pago.', { icon: 'mdi-alert-circle' });
                return;
            }

            this.loading = true;
            const data = new FormData();
            data.append('payout_account_id', this.approveForm.payout_account_id);
            data.append('payout_reference', this.approveForm.payout_reference);
            data.append('payout_voucher', this.approveForm.payout_voucher);

            axios.post('/api/wallet/withdrawals/' + this.selected.id + '/approve', data).then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.closeDialogs();
                this.getWithdrawals(true);
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible aprobar el retiro.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        submitReject() {
            if (!this.selected) return;

            this.loading = true;
            axios.post('/api/wallet/withdrawals/' + this.selected.id + '/reject', {
                reason: this.reason
            }).then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.closeDialogs();
                this.getWithdrawals(true);
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible rechazar el retiro.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        canReview(item) {
            if (item.status !== 'pending' || !this.user) return false;
            if (this.user.role === 'Administrator') return true;

            return this.user.role === 'Payer' && item.payout_method === 'office' && item.can_review !== false;
        },
        accountsForWithdrawal(item) {
            if (!item || !item.payout_currency) {
                return this.payoutAccounts;
            }

            return this.payoutAccounts.filter(account => !account.currency || account.currency === item.payout_currency);
        },
        statusColor(status) {
            if (status === 'approved') return 'success';
            if (status === 'rejected') return 'error';
            return 'warning';
        },
        feeText(quote, side) {
            const fee = quote && quote.fees ? quote.fees[side] : null;

            if (!fee || !fee.has_fee) {
                return 'Sin comision';
            }

            return fee.formatted;
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>

<style scoped>
.withdrawal-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.withdrawal-fixed-header,
.withdrawal-fixed-footer {
    flex: 0 0 auto;
}

.withdrawal-fixed-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}
</style>
